import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { config } from "../config/const";
import "./admin.css"; // Import the CSS file

const AdminPage = () => {
  const [stories, setStories] = useState([]);
  const [publicStories, setPublicStories] = useState([]);
  const [errorLogs, setErrorLogs] = useState([]);
  const [expandedStoryIds, setExpandedStoryIds] = useState([]); // Track expanded stories
  const [expandedLogIds, setExpandedLogIds] = useState([]);
  const [selectedStories, setSelectedStories] = useState({});
  const [selectedSection, setSelectedSection] = useState("pending"); // Track selected section
  const [adminStories, setAdminStories] = useState([]);
  const [regeneratingIds, setRegeneratingIds] = useState({});

  useEffect(() => {
    const fetchPendingStories = async () => {
      try {
        const token = localStorage.getItem("idToken");
        const response = await axios.get(
          `${config.backend_url}/user/pending-stories`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setStories(response.data.pendingStories);
      } catch (error) {
        console.error("Error fetching pending stories:", error);
        if (error.response.data.message === "Unauthorized") {
          window.location.href = "/";
        }
      }
    };

    const fetchPublicStories = async () => {
      try {
        const email = localStorage.getItem("email");
        console.log(email);
        const response = await axios.get(
          `${config.backend_url}/user/getStoryPublic`,
          {
            params: {
              email: email,
            },
          }
        );
        console.log(response.data.stories);
        setPublicStories(response.data.stories);
      } catch (error) {
        console.error("Error fetching public stories:", error);
        if (error.response.data.message === "Unauthorized") {
          window.location.href = "/";
        }
      }
    };

    const fetchErrorLogs = async () => {
      try {
        const token = localStorage.getItem("idToken");
        const response = await axios.get(
          `${config.backend_url}/user/error-logs?limit=50`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setErrorLogs(response.data.logs || []);
      } catch (error) {
        console.error("Error fetching error logs:", error);
      }
    };

    const fetchAdminStories = async () => {
      try {
        const token = localStorage.getItem("idToken");
        const response = await axios.get(`${config.backend_url}/user/admin/public-stories`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAdminStories(response.data.stories || []);
      } catch (error) {
        console.error("Error fetching admin stories:", error);
      }
    };

    fetchPendingStories();
    fetchPublicStories();
    fetchErrorLogs();
    fetchAdminStories();
  }, []);

  const toggleExpandStory = (storyId) => {
    setExpandedStoryIds((prevState) =>
      prevState.includes(storyId)
        ? prevState.filter((id) => id !== storyId)
        : [...prevState, storyId]
    );
  };

  const handleSelectChange = (storyId, checked) => {
    setSelectedStories((prev) => ({
      ...prev,
      [storyId]: checked,
    }));
  };

  const approveStory = async (storyId) => {
    const token = localStorage.getItem("idToken");
    try {
      await axios.post(
        `${config.backend_url}/user/approve-story`,
        { storyId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setStories(stories.filter((story) => story._id !== storyId));
    } catch (error) {
      console.error("Error approving story:", error);
    }
  };

  const deleteStory = async (storyId) => {
    const token = localStorage.getItem("idToken");
    try {
      await axios.post(
        `${config.backend_url}/user/reject-story`,
        {
          storyId: storyId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setStories(stories.filter((story) => story._id !== storyId));
    } catch (error) {
      console.error("Error deleting story:", error);
    }
  };

  const deletePublicStory = async (storyId) => {
    const token = localStorage.getItem("idToken");
    try {
      await axios.post(
        `${config.backend_url}/user/reject-story`,
        {
          storyId: storyId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setPublicStories(publicStories.filter((story) => story._id !== storyId));
    } catch (error) {
      console.error("Error deleting public story:", error);
    }
  };

  const approveSelectedStories = async () => {
    const token = localStorage.getItem("idToken");
    const selectedIds = Object.keys(selectedStories).filter(
      (storyId) => selectedStories[storyId]
    );
    try {
      for (let storyId of selectedIds) {
        await axios.post(
          `${config.backend_url}/user/approve-story`,
          { storyId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }
      setStories(stories.filter((story) => !selectedIds.includes(story._id)));
    } catch (error) {
      console.error("Error approving stories:", error);
    }
  };

  const deleteSelectedStories = async () => {
    const selectedIds = Object.keys(selectedStories).filter(
      (storyId) => selectedStories[storyId]
    );
    const token = localStorage.getItem("idToken");
    try {
      for (let storyId of selectedIds) {
        await axios.post(
          `${config.backend_url}/user/reject-story`,
          {
            storyId,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }
      setStories(stories.filter((story) => !selectedIds.includes(story._id)));
    } catch (error) {
      console.error("Error deleting stories:", error);
    }
  };

  const handleRegenerateThumbnail = async (storyId) => {
    const token = localStorage.getItem("idToken");
    setRegeneratingIds(prev => ({ ...prev, [storyId]: true }));
    try {
      const response = await axios.post(
        `${config.backend_url}/user/admin/regenerate-thumbnail`,
        { storyId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAdminStories(prev => prev.map(s =>
        s._id === storyId ? { ...s, thumbnail: response.data.thumbnail } : s
      ));
    } catch (error) {
      alert("Failed to regenerate: " + (error.response?.data?.message || error.message));
    } finally {
      setRegeneratingIds(prev => ({ ...prev, [storyId]: false }));
    }
  };

  const deleteSelectedPublicStories = async () => {
    const selectedIds = Object.keys(selectedStories).filter(
      (storyId) => selectedStories[storyId]
    );
    const token = localStorage.getItem("idToken");
    try {
      for (let storyId of selectedIds) {
        await axios.post(
          `${config.backend_url}/user/reject-story`,
          {
            storyId,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }
      setPublicStories(
        publicStories.filter((story) => !selectedIds.includes(story._id))
      );
    } catch (error) {
      console.error("Error deleting public stories:", error);
    }
  };

  return (
    <div className="admin-container">
      <h1>Admin Dashboard</h1>
      <div className="section-toggle">
        <button
          onClick={() => setSelectedSection("pending")}
          className={selectedSection === "pending" ? "active" : ""}
        >
          Pending Stories
        </button>
        <button
          onClick={() => setSelectedSection("public")}
          className={selectedSection === "public" ? "active" : ""}
        >
          Public Stories
        </button>
        <button
          onClick={() => setSelectedSection("errors")}
          className={selectedSection === "errors" ? "active" : ""}
        >
          Error Logs ({errorLogs.length})
        </button>
        <button
          onClick={() => setSelectedSection("images")}
          className={selectedSection === "images" ? "active" : ""}
        >
          Images
        </button>
      </div>

      {selectedSection === "pending" && (
        <>
          <h2>Pending Stories</h2>
          <table className="story-table">
            <thead>
              <tr>
                <th>Select</th>
                <th>Title</th>
                <th>Author</th>
                <th>Expand</th>
                <th>Approve</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {stories.map((story) => (
                <React.Fragment key={story._id}>
                  <tr>
                    <td>
                      <input
                        type="checkbox"
                        checked={!!selectedStories[story._id]}
                        onChange={(e) =>
                          handleSelectChange(story._id, e.target.checked)
                        }
                      />
                    </td>
                    <td>
                      <Link to={`/story/${story._id}`}>{story.title}</Link>
                    </td>
                    <td>{story.user?.name}</td>
                    <td>
                      <button
                        onClick={() => toggleExpandStory(story._id)}
                        className="expand-button"
                      >
                        {expandedStoryIds.includes(story._id)
                          ? "Hide Story"
                          : "Expand Story"}
                      </button>
                    </td>
                    <td>
                      <div className="bulk-actions">
                        <button
                          onClick={() => approveStory(story._id)}
                          className="approve-button"
                        >
                          Approve
                        </button>
                      </div>
                    </td>
                    <td>
                      <div className="bulk-actions">
                        <button
                          onClick={() => deleteStory(story._id)}
                          className="delete-button"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedStoryIds.includes(story._id) && (
                    <tr>
                      <td colSpan="6">
                        <div className="story-content">
                          <p>{story.content}</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          <div className="bulk-actions">
            <button onClick={approveSelectedStories}>Approve Selected</button>
            <button onClick={deleteSelectedStories}>Delete Selected</button>
          </div>
        </>
      )}

      {selectedSection === "public" && (
        <>
          <h2>Public Stories</h2>
          <table className="story-table">
            <thead>
              <tr>
                <th>Select</th>
                <th>Title</th>
                <th>Author</th>
                <th>Expand</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {publicStories.map((story) => (
                <React.Fragment key={story._id}>
                  <tr>
                    <td>
                      <input
                        type="checkbox"
                        checked={!!selectedStories[story._id]}
                        onChange={(e) =>
                          handleSelectChange(story._id, e.target.checked)
                        }
                      />
                    </td>
                    <td>
                      <Link to={`/story/${story._id}`}>{story.title}</Link>
                    </td>
                    <td>{story.user?.name}</td>
                    <td>
                      <button
                        onClick={() => toggleExpandStory(story._id)}
                        className="expand-button"
                      >
                        {expandedStoryIds.includes(story._id)
                          ? "Hide Story"
                          : "Expand Story"}
                      </button>
                    </td>
                    <td>
                      <div className="bulk-actions">
                        <button
                          onClick={() => deletePublicStory(story._id)}
                          className="delete-button"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedStoryIds.includes(story._id) && (
                    <tr>
                      <td colSpan="5">
                        <div className="story-content">
                          <p>{story.content}</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          <div className="bulk-actions">
            <button onClick={deleteSelectedPublicStories}>
              Delete Selected
            </button>
          </div>
        </>
      )}

      {selectedSection === "images" && (
        <>
          <h2>Story Images</h2>
          <p style={{ color: "var(--color-text-secondary)", marginBottom: "1rem" }}>
            Click "Regenerate" to create a new thumbnail for any story using AI. Each takes ~30–45 seconds.
          </p>
          <table className="story-table">
            <thead>
              <tr>
                <th>Thumbnail</th>
                <th>Title</th>
                <th>Views</th>
                <th>Likes</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {adminStories.map((story) => {
                const isBroken = !story.thumbnail || story.thumbnail.includes("dijb37son");
                const isLoading = regeneratingIds[story._id];
                return (
                  <tr key={story._id}>
                    <td>
                      {story.thumbnail && !isBroken ? (
                        <img
                          src={story.thumbnail}
                          alt="thumb"
                          style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 6 }}
                        />
                      ) : (
                        <div style={{
                          width: 60, height: 60, borderRadius: 6,
                          background: "#fee2e2", display: "flex",
                          alignItems: "center", justifyContent: "center",
                          fontSize: "0.65rem", color: "#ef4444", textAlign: "center"
                        }}>
                          {isLoading ? "⏳" : "No Image"}
                        </div>
                      )}
                    </td>
                    <td>
                      <Link to={`/story/${story._id}`}>{story.title}</Link>
                    </td>
                    <td>{story.views}</td>
                    <td>{story.likes}</td>
                    <td>
                      <span style={{
                        padding: "2px 8px", borderRadius: 4, fontSize: "0.8rem",
                        background: isBroken ? "#fee2e2" : "#dcfce7",
                        color: isBroken ? "#ef4444" : "#16a34a"
                      }}>
                        {isBroken ? "Broken" : "OK"}
                      </span>
                    </td>
                    <td>
                      <button
                        className="approve-button"
                        onClick={() => handleRegenerateThumbnail(story._id)}
                        disabled={isLoading}
                        style={{ minWidth: 110 }}
                      >
                        {isLoading ? "Generating..." : "Regenerate"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {selectedSection === "errors" && (
        <>
          <h2>Error Logs (Recent {errorLogs.length})</h2>
          {errorLogs.length === 0 ? (
            <p>No error logs found.</p>
          ) : (
            <table className="story-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Status</th>
                  <th>Error Details</th>
                  <th>Prompt</th>
                </tr>
              </thead>
              <tbody>
                {errorLogs.map((log) => (
                  <React.Fragment key={log._id}>
                    <tr>
                      <td style={{ whiteSpace: "nowrap", fontSize: "0.85rem" }}>
                        {new Date(log.createdAt).toLocaleString()}
                      </td>
                      <td>
                        <span style={{
                          background: log.statusCode === "429" ? "#f59e0b" : "#ef4444",
                          color: "#fff",
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "0.8rem"
                        }}>
                          {log.statusCode || "N/A"}
                        </span>
                      </td>
                      <td style={{ maxWidth: "400px", fontSize: "0.85rem" }}>
                        <div style={{
                          maxHeight: expandedLogIds.includes(log._id) ? "none" : "60px",
                          overflow: "hidden",
                          wordBreak: "break-word"
                        }}>
                          {log.data || "No details"}
                        </div>
                        {log.data && log.data.length > 100 && (
                          <button
                            className="expand-button"
                            onClick={() => setExpandedLogIds(prev =>
                              prev.includes(log._id)
                                ? prev.filter(id => id !== log._id)
                                : [...prev, log._id]
                            )}
                            style={{ fontSize: "0.75rem", marginTop: "4px" }}
                          >
                            {expandedLogIds.includes(log._id) ? "Show Less" : "Show More"}
                          </button>
                        )}
                      </td>
                      <td style={{ maxWidth: "300px", fontSize: "0.8rem" }}>
                        <div style={{
                          maxHeight: expandedLogIds.includes(log._id) ? "none" : "60px",
                          overflow: "hidden",
                          wordBreak: "break-word",
                          color: "#888"
                        }}>
                          {log.prompt ? log.prompt.substring(0, 200) + (log.prompt.length > 200 ? "..." : "") : "N/A"}
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

export default AdminPage;
